import { useState, useEffect, useMemo, useRef } from '@wordpress/element';
// import { DokanModal, DateTimePicker } from '@dokan/components';
import { DokanModal } from '@dokan/components';
import { SearchableSelect, SimpleInput } from '@getdokan/dokan-ui';
import { __ } from '@wordpress/i18n';
import { Popover, DatePicker } from '@wordpress/components';
import { dateI18n, getSettings } from '@wordpress/date';
import { Calendar } from 'lucide-react';

type ShippingDetails = {
    country: string;
    state_name: string;
    address_line_1: string;
    address_line_2: string;
    post_code: string;
    expected_delivery: string;
};

type ShippingDetailsModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onSave: ( shipping: ShippingDetails ) => void;
    shippingDetails: ShippingDetails;
};

const ShippingDetailsModal = ( {
    isOpen,
    onClose,
    onSave,
    shippingDetails,
}: ShippingDetailsModalProps ) => {
    const [ formData, setFormData ] = useState< ShippingDetails >( {
        country: '',
        state_name: '',
        address_line_1: '',
        address_line_2: '',
        post_code: '',
        expected_delivery: '',
    } );

    useEffect( () => {
        if ( shippingDetails ) {
            setFormData( shippingDetails );
        }
    }, [ shippingDetails ] );

    const countries = useMemo( () => {
        // @ts-ignore
        const dokanCountries = dokanAdminDashboard?.countries || {};
        return Object.entries( dokanCountries ).map( ( [ code, name ] ) => ( {
            value: code,
            label: name as string,
        } ) );
    }, [] );

    const states = useMemo( () => {
        if ( ! formData.country ) {
            return [];
        }
        // @ts-ignore
        const dokanStates = dokanAdminDashboard?.states || {};
        const countryStates = dokanStates[ formData.country ] || {};
        return Object.entries( countryStates ).map( ( [ code, name ] ) => ( {
            value: code,
            label: name as string,
        } ) );
    }, [ formData.country ] );

    const handleChange = ( field: keyof ShippingDetails, value: string ) => {
        setFormData( { ...formData, [ field ]: value } );
    };

    const handleSave = () => {
        onSave( formData );
    };

    const [ isDatePickerVisible, setIsDatePickerVisible ] = useState( false );
    const datePickerAnchorRef = useRef< HTMLDivElement >( null );

    const isInvalidDate = ( date: string ) => {
        // @ts-ignore
        const moment = window.moment;
        if ( ! moment ) {
            return false;
        }
        return moment().isAfter( moment( date ), 'day' );
    };

    return (
        <DokanModal
            isOpen={ isOpen }
            onClose={ onClose }
            className="w-[620px]"
            namespace="shipping-details-modal"
            dialogTitle={ __( 'Editing Shipping Details', 'dokan' ) }
            onConfirm={ handleSave }
            confirmButtonText={ __( 'Update', 'dokan' ) }
            cancelButtonText={ __( 'Cancel', 'dokan' ) }
            dialogContent={
                <div className="space-y-5 px-1 py-2">
                    <div className="w-full">
                        { /* eslint-disable-next-line jsx-a11y/label-has-associated-control */ }
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            { __( 'Country', 'dokan' ) }
                        </label>
                        <SearchableSelect
                            value={
                                countries.find(
                                    ( c ) => c.value === formData.country
                                ) || null
                            }
                            onChange={ ( opt: any ) => {
                                setFormData( {
                                    ...formData,
                                    country: opt?.value || '',
                                    state_name: '',
                                } );
                            } }
                            options={ countries }
                            placeholder={ __( 'Select country', 'dokan' ) }
                            isSearchable={ true }
                            className="w-full"
                        />
                    </div>

                    <div className="w-full">
                        { /* eslint-disable-next-line jsx-a11y/label-has-associated-control */ }
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            { __( 'State', 'dokan' ) }
                        </label>
                        <SearchableSelect
                            value={
                                states.find(
                                    ( s ) => s.value === formData.state_name
                                ) ||
                                ( formData.state_name
                                    ? {
                                          value: formData.state_name,
                                          label: formData.state_name,
                                      }
                                    : null )
                            }
                            onChange={ ( opt: any ) =>
                                handleChange( 'state_name', opt?.value || '' )
                            }
                            options={ states }
                            placeholder={ __( 'Select state', 'dokan' ) }
                            isSearchable={ true }
                            className="w-full"
                        />
                    </div>

                    <div>
                        { /* eslint-disable-next-line jsx-a11y/label-has-associated-control */ }
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            { __( 'Address Line 1', 'dokan' ) }
                        </label>
                        <input
                            type="text"
                            value={ formData.address_line_1 }
                            onChange={ ( e ) =>
                                handleChange( 'address_line_1', e.target.value )
                            }
                            placeholder={ __( 'Road, Area etc', 'dokan' ) }
                            className="w-full px-3 py-2.5 text-sm border border-[#e5e7eb] rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                    </div>

                    <div>
                        { /* eslint-disable-next-line jsx-a11y/label-has-associated-control */ }
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            { __( 'Address Line 2', 'dokan' ) }
                        </label>
                        <input
                            type="text"
                            value={ formData.address_line_2 }
                            onChange={ ( e ) =>
                                handleChange( 'address_line_2', e.target.value )
                            }
                            placeholder={ __(
                                'Apartment, Suite etc',
                                'dokan'
                            ) }
                            className="w-full px-3 py-2.5 text-sm border border-[#e5e7eb] rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                    </div>

                    <div>
                        { /* eslint-disable-next-line jsx-a11y/label-has-associated-control */ }
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            { __( 'Expected Delivery Date', 'dokan' ) }
                        </label>
                        <div
                            ref={ datePickerAnchorRef }
                            onClick={ () =>
                                setIsDatePickerVisible( ! isDatePickerVisible )
                            }
                        >
                            <SimpleInput
                                addOnLeft={ <Calendar size="16" /> }
                                className="border rounded px-3 py-1.5 w-full bg-white cursor-pointer"
                                onChange={ () => {} }
                                input={ {
                                    type: 'text',
                                    value: formData.expected_delivery
                                        ? dateI18n(
                                              getSettings().formats.date,
                                              formData.expected_delivery,
                                              getSettings().timezone.string
                                          )
                                        : '',
                                    placeholder: __( 'Date', 'dokan' ),
                                    readOnly: true,
                                } }
                            />
                        </div>
                        { isDatePickerVisible && (
                            <Popover
                                anchor={ datePickerAnchorRef.current }
                                onClose={ () =>
                                    setIsDatePickerVisible( false )
                                }
                                className={ `dokan-layout` }
                            >
                                <div className="p-4">
                                    <DatePicker
                                        currentDate={
                                            formData.expected_delivery
                                        }
                                        onChange={ ( newDate ) => {
                                            handleChange(
                                                'expected_delivery',
                                                newDate.split( 'T' )[ 0 ] || ''
                                            );
                                            setIsDatePickerVisible( false );
                                        } }
                                        isInvalidDate={ ( d ) =>
                                            isInvalidDate( d )
                                        }
                                    />
                                </div>
                            </Popover>
                        ) }
                    </div>
                </div>
            }
        />
    );
};

export default ShippingDetailsModal;
